import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hash = window.location.pathname.split('/verify/')[1];
    if (!hash) { setError('Kein Zertifikat angegeben'); setLoading(false); return; }
    fetch(`${API}/api/surveys/verify/${hash}`)
      .then(r => { if (!r.ok) throw new Error('Nicht gefunden'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Zertifikat nicht gefunden oder ungültig'); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center', color: '#6b7280' }}>Wird geprüft...</div>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc', padding: 16 }}>
      <div style={{ textAlign: 'center', maxWidth: 420, padding: 24 }}>
        <XCircle size={48} color="#EF4444" style={{ marginBottom: 12 }} />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>Ungültig</h1>
        <p style={{ color: '#6b7280', fontSize: 14 }}>{error}</p>
      </div>
    </div>
  );

  const valid = data?.valid;
  const formatDate = (s) => {
    if (!s) return '–';
    try { const p = s.split('T')[0].split('-'); return `${p[2]}.${p[1]}.${p[0]}`; } catch { return s; }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', background: '#f8fafc', padding: '16px 12px' }}>
      <div style={{ maxWidth: 480, width: '100%', background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }} data-testid="verify-page">
        {/* Header */}
        <div style={{ padding: '24px 20px', background: valid ? '#059669' : '#DC2626', color: '#fff', textAlign: 'center' }}>
          {valid ? <CheckCircle size={40} style={{ marginBottom: 6 }} /> : <XCircle size={40} style={{ marginBottom: 6 }} />}
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{valid ? 'Zertifikat gültig' : 'Zertifikat ungültig'}</h1>
          <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Shield size={12} />
            Digital zertifiziert
          </div>
        </div>

        {/* Details */}
        <div style={{ padding: '20px 16px' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Zertifikat-Nr.</div>
            <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'monospace', color: '#1f2937' }} data-testid="verify-hash">{data.verification_hash}</div>
          </div>

          {/* Info grid - stacks on narrow screens */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px', marginBottom: 16 }}>
            <div style={{ gridColumn: data.verein?.length > 25 ? '1 / -1' : undefined }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Verein</div>
              <div style={{ fontSize: 13, fontWeight: 500, wordBreak: 'break-word' }} data-testid="verify-verein">{data.verein}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Club-ID</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{data.club_id}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Erhebung</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{data.survey_title}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Fixiert am</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{formatDate(data.fixed_at)}</div>
            </div>
          </div>

          {/* Data table - compact for mobile */}
          {data.data && data.data.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Gemeldete Daten</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #e5e7eb', fontSize: 11 }}>Angebotsart</th>
                    <th style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e5e7eb', fontSize: 11 }}>Gr.</th>
                    <th style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e5e7eb', fontSize: 11 }}>W</th>
                    <th style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e5e7eb', fontSize: 11 }}>M</th>
                    <th style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #e5e7eb', fontSize: 11 }}>Ges.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((e, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 8px', fontSize: 12 }}>{e.offer_type}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>{e.group_count}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>{e.female_count}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>{e.male_count}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 600 }}>{e.total_count}</td>
                    </tr>
                  ))}
                  {data.data.length > 1 && (
                    <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>
                      <td style={{ padding: '6px 8px', fontSize: 12 }}>Gesamt</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>{data.data.reduce((s, e) => s + (e.group_count || 0), 0)}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>{data.data.reduce((s, e) => s + (e.female_count || 0), 0)}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>{data.data.reduce((s, e) => s + (e.male_count || 0), 0)}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>{data.data.reduce((s, e) => s + (e.total_count || 0), 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
            Bestandserhebung {data.survey_title?.match(/\d{4}/)?.[0] || ''}
          </div>
        </div>
      </div>
    </div>
  );
}
